import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { loadRecaptchaScript, executeRecaptcha } from '../services/recaptcha';
import SEO from '../components/SEO';
import './CustomerAuth.css';

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, isAuthenticated } = useCustomerAuth();
  const { storeDefaultPhonePrefix } = useSiteSettings();
  const formStartTime = useRef(Math.floor(Date.now() / 1000));

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    website: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRecaptchaScript().catch(() => {});
  }, []);

  if (isAuthenticated) {
    navigate('/account', { replace: true });
    return null;
  }

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim() || !formData.email.trim() || !formData.password) {
      setError(t('auth.allFieldsRequired', 'Please fill in all required fields'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsDontMatch'));
      return;
    }

    setLoading(true);
    try {
      const recaptchaToken = await executeRecaptcha('register');
      const result = await register(
        formData.name,
        formData.email,
        formData.password,
        formData.phone,
        {
          recaptchaToken,
          website: formData.website,
          formStartTime: formStartTime.current,
        }
      );

      if (result.success) {
        navigate('/login', {
          state: { message: t('auth.registrationSuccess', 'Account created successfully! You can now sign in.') }
        });
      } else {
        setError(result.error);
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="customer-auth-page">
      <SEO title={t('auth.register', 'Create Account')} />

      <div className="auth-card">
        <div className="auth-header">
          <h1>{t('auth.registerTitle', 'Create Account')}</h1>
          <p>{t('auth.registerSubtitle', 'Sign up to track orders, save addresses, and checkout faster')}</p>
        </div>

        {error && <div className="auth-error-banner">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <input
            type="text"
            name="website"
            value={formData.website}
            onChange={handleChange}
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
          />

          <div className="auth-form-group">
            <label htmlFor="name">{t('checkout.name', 'Full Name')} *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              required
              autoFocus
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="email">{t('checkout.email', 'Email')} *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
              required
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="phone">{t('checkout.phone', 'Phone')}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={`${storeDefaultPhonePrefix || '+1'} XXX XXX XXXX`}
            />
          </div>

          <div className="auth-form-row">
            <div className="auth-form-group">
              <label htmlFor="password">{t('auth.password', 'Password')} *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Min. 6 characters"
                required
                minLength={6}
              />
            </div>

            <div className="auth-form-group">
              <label htmlFor="confirmPassword">{t('auth.confirmPassword', 'Confirm Password')} *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Repeat password"
                required
              />
            </div>
          </div>

          <button type="submit" className="auth-submit-btn" disabled={loading}>
            {loading ? t('auth.creating', 'Creating account...') : t('auth.registerBtn', 'Create Account')}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {t('auth.alreadyHaveAccount', 'Already have an account?')}{' '}
            <Link to="/login" className="auth-link-inline">{t('auth.loginBtn', 'Sign In')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
